/**
 * Grabación de audio del viaje (como Uber): se guarda SOLO en este teléfono y nadie puede escucharla
 * aquí; únicamente se envía si el usuario la adjunta a un reporte de seguridad. Se borra sola a los 7 días.
 */

const DB = 'tcrd-recordings';
const STORE = 'rides';
const KEEP_MS = 7 * 86_400_000;
/** Máximo por viaje: el servidor acepta archivos de hasta 10 MB (a 24 kbps son ~55 min). */
const MAX_MS = 50 * 60_000;

interface Recording {
  rideId: string;
  mime: string;
  startedAt: number;
  endedAt: number;
  blob: Blob;
}

export interface RecorderState {
  rideId: string | null;
  recording: boolean;
  startedAt: number | null;
  error: string | null;
}

function db(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'rideId' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const r = fn(d.transaction(STORE, mode).objectStore(STORE));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

const save = (r: Recording) => tx('readwrite', (s) => s.put(r));
export const getRecording = (rideId: string) => tx<Recording | undefined>('readonly', (s) => s.get(rideId)).catch(() => undefined);
export const deleteRecording = (rideId: string) => tx('readwrite', (s) => s.delete(rideId)).catch(() => undefined);

/** Borra las grabaciones de más de 7 días. */
export async function cleanupRecordings() {
  try {
    const all = await tx<Recording[]>('readonly', (s) => s.getAll());
    for (const r of all) if (Date.now() - r.endedAt > KEEP_MS) await deleteRecording(r.rideId);
  } catch {
    /* sin IndexedDB (modo privado): no hay nada que limpiar */
  }
}

export const recordingSupported = () => typeof window !== 'undefined' && 'MediaRecorder' in window && !!navigator.mediaDevices?.getUserMedia && 'indexedDB' in window;

let state: RecorderState = { rideId: null, recording: false, startedAt: null, error: null };
const listeners = new Set<(s: RecorderState) => void>();
const set = (s: Partial<RecorderState>) => {
  state = { ...state, ...s };
  listeners.forEach((l) => l(state));
};
export const recorderState = () => state;
export function subscribeRecorder(fn: (s: RecorderState) => void) {
  listeners.add(fn);
  fn(state);
  return () => void listeners.delete(fn);
}

let media: MediaRecorder | null = null;
let stream: MediaStream | null = null;
let chunks: Blob[] = [];
let limit: ReturnType<typeof setTimeout> | null = null;

function pickMime() {
  for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']) if (MediaRecorder.isTypeSupported?.(m)) return m;
  return '';
}

export async function startRecording(rideId: string) {
  if (state.recording) return;
  if (!recordingSupported()) return set({ error: 'Este dispositivo no permite grabar audio desde la app.' });
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  } catch {
    return set({ error: 'Permite el uso del micrófono para grabar el viaje.' });
  }
  const mime = pickMime();
  media = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: 24_000 });
  chunks = [];
  const startedAt = Date.now();
  // Se guarda cada 10 s: si la app se cierra, lo grabado hasta ahí no se pierde.
  media.ondataavailable = (e) => {
    if (!e.data.size) return;
    chunks.push(e.data);
    void save({ rideId, mime: media?.mimeType || mime || 'audio/webm', startedAt, endedAt: Date.now(), blob: new Blob(chunks, { type: media?.mimeType || mime || 'audio/webm' }) });
  };
  media.onstop = () => {
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    media = null;
    if (limit) clearTimeout(limit);
    set({ recording: false, startedAt: null });
  };
  media.start(10_000);
  limit = setTimeout(() => stopRecording(), MAX_MS);
  set({ rideId, recording: true, startedAt, error: null });
}

export function stopRecording() {
  if (media && media.state !== 'inactive') media.stop();
}
